<div class="bp-page">
    <p-toast></p-toast>

    <!-- Page header -->
    <div class="bp-header">
        <div>
            <h1 class="bp-title">Sales Pipeline</h1>
            <span class="bp-subtitle">Real-time view of every owner lead in motion</span>
        </div>
        <div class="bp-header-actions">
            <span *ngIf="lastRefresh" class="bp-last-refresh">
                Last refreshed {{ lastRefresh | date:'MMM d, h:mm a' }}
            </span>
            <button mat-stroked-button (click)="loadAll()" [disabled]="metricsLoading || tableLoading" class="bp-btn-outline">
                <i class="pi pi-refresh"></i> Refresh
            </button>
            <button class="bp-btn-outline" (click)="exportCsv()" [disabled]="filteredRows.length === 0" title="Download current view as CSV">
                <i class="pi pi-download"></i> Export CSV
            </button>
            <a routerLink="/owner-lead-profile-list" mat-raised-button class="bp-btn-cta">+ New Lead</a>
        </div>
    </div>

    <!-- Agent filter toggle -->
    <div class="bp-agent-filter">
        <button class="bp-filter-btn"
                [class.bp-filter-btn-active]="agentFilter === 'all'"
                (click)="setAgentFilter('all')">All</button>
        <button class="bp-filter-btn"
                [class.bp-filter-btn-active]="agentFilter === 'me'"
                (click)="setAgentFilter('me')">Me</button>
        <button class="bp-filter-btn"
                [class.bp-filter-btn-active]="agentFilter === 'team'"
                (click)="setAgentFilter('team')"
                title="Filter by your team members">My Team</button>
    </div>

    <!-- Specific-agent filter chip (set via leaderboard click-through) -->
    <div *ngIf="agentFilter === 'specific'" class="bp-specific-agent-chip">
        <i class="pi pi-user"></i>
        Filtered by: <strong>{{ selectedAgentName || ('Agent #' + selectedAgentId) }}</strong>
        <button type="button" class="bp-chip-clear" (click)="clearSpecificAgentFilter()" title="Clear filter">
            <i class="pi pi-times"></i>
        </button>
    </div>

    <!-- Top metrics row -->
    <div class="bp-metrics">
        <div class="bp-stat-card">
            <span class="bp-stat-label">Active Leads</span>
            <span class="bp-stat-value">{{ totalActiveLeads }}</span>
            <span class="bp-stat-hint">Excluding Loss / Do Not Want</span>
        </div>
        <div class="bp-stat-card">
            <span class="bp-stat-label">Proformas Sent</span>
            <span class="bp-stat-value">{{ proformasSentMonth }}</span>
            <span class="bp-stat-hint">Last {{ metricsWindowDays }} days</span>
        </div>
        <div class="bp-stat-card bp-stat-hot">
            <span class="bp-stat-label">Hot Activity</span>
            <span class="bp-stat-value">{{ leadsWithViewsThisWeek }}</span>
            <span class="bp-stat-hint">Leads with proforma views ({{ metricsWindowDays }}d)</span>
        </div>
        <div class="bp-stat-card bp-stat-win">
            <span class="bp-stat-label">Signed / Converted</span>
            <span class="bp-stat-value">{{ convertedThisMonth }}</span>
            <span class="bp-stat-hint">Last {{ metricsWindowDays }} days</span>
        </div>
    </div>

    <!-- Pipeline funnel -->
    <div class="bp-section">
        <div class="bp-section-header">
            <h2 class="bp-section-title">Pipeline Funnel</h2>
            <span class="bp-section-meta">{{ funnelTotal }} total · click a stage to filter</span>
        </div>
        <div class="bp-funnel">
            <ng-container *ngFor="let stage of funnelStages; let i = index; let last = last">
                <div class="bp-funnel-stage"
                     [class.bp-funnel-active]="selectedStage === stage.value"
                     (click)="selectStage(stage.value)">
                    <div class="bp-funnel-label">{{ stage.label }}</div>
                    <div class="bp-funnel-bar-wrap">
                        <div class="bp-funnel-bar"
                             [style.width]="funnelBarWidth(stage)"
                             [style.background]="stage.color">
                            <span class="bp-funnel-count">{{ stage.count }}</span>
                        </div>
                    </div>
                </div>
                <div *ngIf="!last" class="bp-funnel-conv">
                    <i class="pi pi-arrow-right"></i>
                    <span>{{ funnelConversion(i) }}</span>
                </div>
            </ng-container>
        </div>
        <div *ngIf="selectedStage !== null" class="bp-filter-banner">
            Filtering: <strong>{{ getLeadStatusLabel(selectedStage) }}</strong>
            <button class="bp-clear-filter" (click)="selectStage(null)">Clear</button>
        </div>
    </div>

    <!-- Pipeline Velocity panel -->
    <div class="bp-velocity-panel">
        <div class="bp-velocity-header" (click)="velocityPanelOpen = !velocityPanelOpen">
            <span>Pipeline Velocity (Last 90 Days)</span>
            <i class="pi" [ngClass]="velocityPanelOpen ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
        </div>
        <div class="bp-velocity-body" *ngIf="velocityPanelOpen">
            <div *ngIf="velocityLoading" class="bp-velocity-loading">Loading...</div>
            <table class="bp-velocity-table" *ngIf="!velocityLoading && velocityStages.length > 0">
                <thead>
                    <tr>
                        <th>Stage</th>
                        <th>Avg Days</th>
                        <th>Leads</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let s of velocityStages">
                        <td>{{ s.label }}</td>
                        <td>{{ s.avgDays }}</td>
                        <td>{{ s.countIn }}</td>
                    </tr>
                </tbody>
            </table>
            <div *ngIf="!velocityLoading && velocityStages.length === 0" class="bp-velocity-empty">No data for selected window.</div>
        </div>
    </div>

    <!-- Two-column: table + activity feed -->
    <div class="bp-main-grid">

        <!-- Leads table -->
        <div class="bp-section bp-table-section">
            <div class="bp-section-header">
                <h2 class="bp-section-title">Leads</h2>
                <span class="bp-section-meta">{{ filteredRows.length }} shown</span>
            </div>

            <!-- Search bar + date range filter -->
            <div class="bp-search-filter-row">
                <div class="bp-search-bar">
                    <i class="pi pi-search bp-search-icon"></i>
                    <input type="text"
                           class="bp-search-input"
                           placeholder="Search by owner, property, agent, or status…"
                           [(ngModel)]="searchQuery"
                           (ngModelChange)="onSearchChange()" />
                    <button *ngIf="searchQuery" class="bp-search-clear" (click)="clearSearch()" title="Clear search">
                        <i class="pi pi-times"></i>
                    </button>
                </div>
                <select class="bp-date-filter" [(ngModel)]="dateRangeFilter" (ngModelChange)="setDateRange($event)">
                    <option value="all">All Time</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="quarter">This Quarter</option>
                </select>
            </div>

            <div class="bp-table-wrap">
                <p-table [value]="filteredRows"
                         [paginator]="true"
                         [rows]="25"
                         [loading]="tableLoading"
                         [rowsPerPageOptions]="[10, 25, 50, 100]"
                         styleClass="bp-table"
                         dataKey="ownerLeadProfileId"
                         [expandedRowKeys]="expandedRowKeys">
                    <ng-template pTemplate="header">
                        <tr>
                            <th style="width: 3rem"></th>
                            <th style="width: 2.5rem; text-align: center" (click)="$event.stopPropagation()">
                                <input type="checkbox"
                                       class="bp-checkbox"
                                       [checked]="selectedRows.length === filteredRows.length && filteredRows.length > 0"
                                       [indeterminate]="selectedRows.length > 0 && selectedRows.length < filteredRows.length"
                                       (change)="toggleSelectAll()"
                                       title="Select all" />
                            </th>
                            <th pSortableColumn="ownerName">Owner <p-sortIcon field="ownerName"></p-sortIcon></th>
                            <th pSortableColumn="propertyName">Property <p-sortIcon field="propertyName"></p-sortIcon></th>
                            <th pSortableColumn="leadStatus">Status <p-sortIcon field="leadStatus"></p-sortIcon></th>
                            <th>Agent</th>
                            <th pSortableColumn="leadScore">Score <p-sortIcon field="leadScore"></p-sortIcon></th>
                            <th pSortableColumn="proformaSent">Proforma <p-sortIcon field="proformaSent"></p-sortIcon></th>
                            <th pSortableColumn="lastViewed">Last Viewed <p-sortIcon field="lastViewed"></p-sortIcon></th>
                            <th pSortableColumn="daysActive">Days <p-sortIcon field="daysActive"></p-sortIcon></th>
                            <th>Actions</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row let-expanded="expanded">
                        <tr [class.bp-row-hot]="row.hot"
                            [class.bp-row-stale]="row.isStale"
                            [class.bp-row-expanded]="isRowExpanded(row)"
                            (click)="toggleRow(row)"
                            style="cursor:pointer">
                            <td class="bp-chevron-cell">
                                <i class="pi"
                                   [class.pi-chevron-down]="isRowExpanded(row)"
                                   [class.pi-chevron-right]="!isRowExpanded(row)"
                                   [class.bp-chevron-open]="isRowExpanded(row)"></i>
                            </td>
                            <td style="text-align: center" (click)="$event.stopPropagation()">
                                <input type="checkbox"
                                       class="bp-checkbox"
                                       [checked]="isRowSelected(row)"
                                       (click)="toggleRowSelection($event, row)" />
                            </td>
                            <td>
                                <div class="bp-cell-owner">
                                    <span class="bp-cell-strong">{{ row.ownerName }}</span>
                                    <span *ngIf="row.isStale" class="bp-stale-badge" title="No update in {{ row.daysSinceUpdate }} days">
                                        <i class="pi pi-clock"></i> Stale
                                    </span>
                                </div>
                            </td>
                            <td>{{ row.propertyName }}</td>

                            <!-- Inline status change -->
                            <td class="bp-status-cell" (click)="$event.stopPropagation()">
                                <div class="bp-status-wrapper">
                                    <span [class]="statusBadgeClass(row.leadStatus)"
                                          class="bp-badge-clickable"
                                          (click)="openStatusMenu($event, row)"
                                          [title]="statusUpdating[row.ownerLeadProfileId] ? 'Saving…' : 'Click to change status'">
                                        <ng-container *ngIf="!statusUpdating[row.ownerLeadProfileId]">
                                            {{ row.leadStatusLabel }}
                                            <i class="pi pi-angle-down bp-badge-caret"></i>
                                        </ng-container>
                                        <i *ngIf="statusUpdating[row.ownerLeadProfileId]" class="pi pi-spin pi-spinner" style="font-size:11px"></i>
                                    </span>
                                    <div *ngIf="statusMenuOpenId === row.ownerLeadProfileId" class="bp-status-menu" (click)="$event.stopPropagation()">
                                        <div *ngFor="let s of ownerLeadService.leadStatuses"
                                             class="bp-status-option"
                                             [class.bp-status-option-active]="s.value === row.leadStatus"
                                             (click)="selectStatus($event, row, s)">
                                            {{ s.name }}
                                        </div>
                                    </div>
                                </div>
                            </td>

                            <!-- Inline agent assign -->
                            <td class="bp-agent-cell" (click)="$event.stopPropagation()">
                                <div class="bp-agent-wrapper">
                                    <span class="bp-agent-btn"
                                          (click)="openAgentMenu($event, row)"
                                          [title]="agentUpdating[row.ownerLeadProfileId] ? 'Saving…' : 'Click to assign agent'">
                                        <ng-container *ngIf="!agentUpdating[row.ownerLeadProfileId]">
                                            {{ row.assignedAgent }}
                                            <i class="pi pi-angle-down bp-badge-caret" style="opacity:.5"></i>
                                        </ng-container>
                                        <i *ngIf="agentUpdating[row.ownerLeadProfileId]" class="pi pi-spin pi-spinner" style="font-size:11px"></i>
                                    </span>
                                    <div *ngIf="agentMenuOpenId === row.ownerLeadProfileId" class="bp-agent-menu" (click)="$event.stopPropagation()">
                                        <div class="bp-agent-option bp-agent-option-unassign"
                                             *ngIf="row.assignedAgentId"
                                             (click)="unassignAgent($event, row)">
                                            <i class="pi pi-times-circle"></i> Unassign
                                        </div>
                                        <div *ngFor="let agent of salesAgents"
                                             class="bp-agent-option"
                                             [class.bp-agent-option-active]="agent.userId === row.assignedAgentId"
                                             (click)="selectAgent($event, row, agent)">
                                            {{ agent.firstName }} {{ agent.lastName }}
                                        </div>
                                        <div *ngIf="salesAgents.length === 0" class="bp-agent-empty">No agents found</div>
                                    </div>
                                </div>
                            </td>

                            <!-- Lead score -->
                            <td>
                                <span [class]="scoreClass(row.leadScore)" [title]="'Lead score: ' + row.leadScore + '/100'">
                                    {{ row.leadScore }}
                                </span>
                            </td>

                            <td>
                                <span *ngIf="row.proformaSent">{{ row.proformaSent | date:'MMM d' }}</span>
                                <span *ngIf="!row.proformaSent" class="bp-cell-muted">—</span>
                            </td>
                            <td>
                                <ng-container *ngIf="row.lastViewed">
                                    <span>{{ row.lastViewed | date:'MMM d' }}</span>
                                    <span class="bp-view-count" *ngIf="row.viewCount > 0"> ({{ row.viewCount }})</span>
                                </ng-container>
                                <span *ngIf="!row.lastViewed" class="bp-cell-muted">—</span>
                            </td>
                            <td>{{ row.daysActive }}</td>
                            <td class="bp-cell-actions" (click)="$event.stopPropagation()">
                                <button class="bp-btn-link" (click)="viewProfile(row)">View</button>
                                <button class="bp-btn-link bp-btn-link-secondary" (click)="openProforma(row)">Proforma</button>
                                <button class="bp-btn-link bp-btn-link-note" (click)="openNotePanel($event, row)" title="Log activity">
                                    <i class="pi pi-plus-circle"></i>
                                </button>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="rowexpansion" let-row>
                        <tr *ngIf="isRowExpanded(row)">
                            <td colspan="11" class="bp-expansion-cell">
                                <div class="bp-sub-panel">
                                    <div class="bp-sub-header">
                                        <h3 class="bp-sub-title">Properties &amp; Proformas</h3>
                                        <span class="bp-sub-meta">{{ row.properties.length }} linked</span>
                                    </div>

                                    <!-- Properties sub-table -->
                                    <ng-container *ngIf="row.properties.length > 0; else noProperties">
                                        <table class="bp-prop-table">
                                            <thead>
                                                <tr>
                                                    <th>Property</th>
                                                    <th>Development</th>
                                                    <th>Beds / Baths</th>
                                                    <th>Est. Value</th>
                                                    <th>Proforma</th>
                                                    <th>Engagement</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr *ngFor="let prop of row.properties; let i = index"
                                                    [class.bp-prop-row-alt]="i % 2 === 0">
                                                    <td class="bp-prop-name">{{ prop.displayName }}</td>
                                                    <td>{{ prop.developmentName }}</td>
                                                    <td>
                                                        <span *ngIf="prop.bedroomNum != null">{{ prop.bedroomNum }} BR</span>
                                                        <span *ngIf="prop.bedroomNum != null && prop.bathroomNum != null"> / </span>
                                                        <span *ngIf="prop.bathroomNum != null">{{ prop.bathroomNum }} BA</span>
                                                        <span *ngIf="prop.bedroomNum == null && prop.bathroomNum == null" class="bp-cell-muted">—</span>
                                                    </td>
                                                    <td>
                                                        <span *ngIf="prop.subjValue != null">{{ prop.subjValue | currency:'USD':'symbol':'1.0-0' }}</span>
                                                        <span *ngIf="prop.subjValue == null" class="bp-cell-muted">—</span>
                                                    </td>
                                                    <td>
                                                        <span class="bp-proforma-status"
                                                              [class.bp-proforma-sent]="prop.proformaSent"
                                                              [class.bp-proforma-draft]="prop.proformaId && !prop.proformaSent"
                                                              [class.bp-proforma-none]="!prop.proformaId">
                                                            {{ proformaStatusLabel(prop) }}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span [class]="engagementBadgeClass(prop)">{{ engagementBadgeLabel(prop) }}</span>
                                                    </td>
                                                    <td class="bp-prop-actions">
                                                        <button class="bp-btn-link" (click)="openPropertyLead(prop)" title="Open property lead">Property</button>
                                                        <button *ngIf="prop.proformaId" class="bp-btn-link" (click)="openPropertyProforma(prop)" title="Open proforma">Open Proforma</button>
                                                        <button *ngIf="!prop.proformaId" class="bp-btn-link" (click)="createProformaForLead(row, prop)" title="Create proforma for this property">Create Proforma</button>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </ng-container>
                                    <ng-template #noProperties>
                                        <div class="bp-sub-empty">No properties linked to this lead.</div>
                                    </ng-template>

                                    <!-- Log activity panel -->
                                    <div class="bp-note-panel" [class.bp-note-panel-open]="noteOpenId === row.ownerLeadProfileId">
                                        <div class="bp-note-header">
                                            <span class="bp-note-title">Log Activity</span>
                                            <button class="bp-note-close" (click)="closeNotePanel()" title="Close">
                                                <i class="pi pi-times"></i>
                                            </button>
                                        </div>
                                        <div class="bp-note-body">
                                            <div class="bp-note-type-row">
                                                <button *ngFor="let nt of noteTypes"
                                                        class="bp-note-type-btn"
                                                        [class.bp-note-type-active]="noteType === nt.value"
                                                        (click)="noteType = nt.value">
                                                    {{ nt.name }}
                                                </button>
                                            </div>
                                            <textarea class="bp-note-textarea"
                                                      [(ngModel)]="noteText"
                                                      placeholder="Add a note about this lead…"
                                                      rows="3"
                                                      [disabled]="noteSaving"></textarea>
                                            <div class="bp-note-footer">
                                                <button class="bp-note-save-btn"
                                                        (click)="saveNote(row)"
                                                        [disabled]="!noteText.trim() || noteSaving">
                                                    <i class="pi" [class.pi-check]="!noteSaving" [class.pi-spin]="noteSaving" [class.pi-spinner]="noteSaving"></i>
                                                    {{ noteSaving ? 'Saving…' : 'Save' }}
                                                </button>
                                                <button class="bp-note-cancel-btn" (click)="closeNotePanel()">Cancel</button>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Follow-up scheduling -->
                                    <div class="bp-followup-panel" *ngIf="followUpOpenId === row.ownerLeadProfileId" (click)="$event.stopPropagation()">
                                        <label class="bp-followup-label">Follow-up Date</label>
                                        <input type="date" class="bp-followup-date" [(ngModel)]="followUpDate" [min]="today" />
                                        <textarea class="bp-followup-note" [(ngModel)]="followUpNote" placeholder="Note (optional)" rows="2"></textarea>
                                        <div class="bp-followup-actions">
                                            <button class="bp-followup-save" (click)="saveFollowUp(row)" [disabled]="!followUpDate || followUpSaving">
                                                {{ followUpSaving ? 'Saving...' : 'Schedule' }}
                                            </button>
                                            <button class="bp-followup-cancel" (click)="followUpOpenId = null">Cancel</button>
                                        </div>
                                    </div>

                                    <!-- Message thread (read-only) -->
                                    <div class="bp-messages-panel">
                                        <div class="bp-messages-header">
                                            <span class="bp-messages-title">Messages</span>
                                            <span class="bp-messages-meta" *ngIf="leadMessages[row.ownerLeadProfileId]">
                                                {{ leadMessages[row.ownerLeadProfileId].length }} sent
                                            </span>
                                        </div>
                                        <div *ngIf="leadMessagesLoading[row.ownerLeadProfileId]" class="bp-messages-loading">
                                            Loading messages…
                                        </div>
                                        <div *ngIf="!leadMessagesLoading[row.ownerLeadProfileId]
                                                    && leadMessages[row.ownerLeadProfileId]
                                                    && leadMessages[row.ownerLeadProfileId].length === 0"
                                             class="bp-messages-empty">
                                            No messages sent to this lead yet.
                                        </div>
                                        <ul *ngIf="!leadMessagesLoading[row.ownerLeadProfileId]
                                                   && leadMessages[row.ownerLeadProfileId]
                                                   && leadMessages[row.ownerLeadProfileId].length > 0"
                                            class="bp-messages-list">
                                            <li *ngFor="let msg of leadMessages[row.ownerLeadProfileId]" class="bp-messages-row">
                                                <div class="bp-messages-row-head">
                                                    <span class="bp-messages-channel bp-messages-channel-{{ (msg.channel || 'EMAIL').toLowerCase() }}">
                                                        {{ msg.channel || 'EMAIL' }}
                                                    </span>
                                                    <span class="bp-messages-subject">{{ msg.subject || '(no subject)' }}</span>
                                                    <span class="bp-messages-date">{{ msg.sentDate | date:'short' }}</span>
                                                </div>
                                                <div *ngIf="msg.bodyPreview" class="bp-messages-preview">{{ msg.bodyPreview }}</div>
                                            </li>
                                        </ul>
                                    </div>

                                    <!-- Footer: link property + log activity toggle -->
                                    <div class="bp-sub-footer">
                                        <button class="bp-btn-link-property" (click)="linkProperty(row)">
                                            <i class="pi pi-plus-circle"></i> Link Property
                                        </button>
                                        <button *ngIf="!row.proformaId" class="bp-btn-log-activity" (click)="createProformaForLead(row)">
                                            <i class="pi pi-plus"></i> Create Proforma
                                        </button>
                                        <button class="bp-followup-btn" (click)="openFollowUp(row); $event.stopPropagation()">
                                            <i class="pi pi-clock"></i> Set Follow-up
                                        </button>
                                        <button class="bp-action-btn" (click)="openCompose(row); $event.stopPropagation()">
                                            <i class="pi pi-envelope"></i> Email
                                        </button>
                                        <button class="bp-action-btn" (click)="openCompose(row); $event.stopPropagation()">
                                            <i class="pi pi-mobile"></i> SMS
                                        </button>
                                        <button class="bp-btn-log-activity"
                                                [class.bp-btn-log-active]="noteOpenId === row.ownerLeadProfileId"
                                                (click)="openNotePanel($event, row)">
                                            <i class="pi pi-pencil"></i>
                                            {{ noteOpenId === row.ownerLeadProfileId ? 'Close Activity Log' : 'Log Activity' }}
                                        </button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="11" class="bp-empty">No leads match the current filter.</td>
                        </tr>
                    </ng-template>
                </p-table>
            </div>
        </div>

        <!-- Activity feed -->
        <aside class="bp-section bp-activity">
            <div class="bp-section-header">
                <h2 class="bp-section-title">Recent Activity</h2>
            </div>
            <div *ngIf="activityLoading" class="bp-activity-loading">Loading activity…</div>
            <div *ngIf="!activityLoading && activityFeed.length === 0" class="bp-empty">No recent activity.</div>
            <ul *ngIf="!activityLoading && activityFeed.length > 0" class="bp-activity-list">
                <li *ngFor="let item of activityFeed" class="bp-activity-row">
                    <i [class]="item.icon + ' bp-activity-icon'"></i>
                    <div class="bp-activity-body">
                        <div class="bp-activity-text">{{ item.text }}</div>
                        <div class="bp-activity-date">{{ item.date | date:'MMM d, h:mm a' }}</div>
                    </div>
                </li>
            </ul>
        </aside>

    </div>

    <!-- Bulk action bar (appears when rows are selected) -->
    <div class="bp-bulk-bar" *ngIf="selectedRows.length > 0" (click)="$event.stopPropagation()">
        <span class="bp-bulk-count">{{ selectedRows.length }} selected</span>

        <!-- Bulk status -->
        <div class="bp-bulk-action-wrap">
            <button class="bp-bulk-btn" (click)="bulkStatusMenuOpen = !bulkStatusMenuOpen; bulkAgentMenuOpen = false; $event.stopPropagation()" [disabled]="bulkUpdating">
                <i class="pi" [class.pi-tag]="!bulkUpdating" [class.pi-spin]="bulkUpdating" [class.pi-spinner]="bulkUpdating"></i>
                Change Status
                <i class="pi pi-angle-up" style="font-size:10px"></i>
            </button>
            <div *ngIf="bulkStatusMenuOpen" class="bp-bulk-menu">
                <div *ngFor="let s of ownerLeadService.leadStatuses"
                     class="bp-bulk-menu-item"
                     (click)="applyBulkStatus(s); $event.stopPropagation()">
                    {{ s.name }}
                </div>
            </div>
        </div>

        <!-- Bulk agent assign -->
        <div class="bp-bulk-action-wrap">
            <button class="bp-bulk-btn" (click)="bulkAgentMenuOpen = !bulkAgentMenuOpen; bulkStatusMenuOpen = false; $event.stopPropagation()" [disabled]="bulkUpdating || salesAgents.length === 0">
                <i class="pi pi-user"></i>
                Assign Agent
                <i class="pi pi-angle-up" style="font-size:10px"></i>
            </button>
            <div *ngIf="bulkAgentMenuOpen" class="bp-bulk-menu">
                <div *ngFor="let agent of salesAgents"
                     class="bp-bulk-menu-item"
                     (click)="applyBulkAgent(agent); $event.stopPropagation()">
                    {{ agent.firstName }} {{ agent.lastName }}
                </div>
            </div>
        </div>

        <button class="bp-bulk-clear" (click)="clearSelection()">
            <i class="pi pi-times"></i> Clear
        </button>
    </div>

    <app-bizdev-compose-dialog
        [(visible)]="composeVisible"
        [row]="composeRow"
        (sent)="onComposeSent($event)">
    </app-bizdev-compose-dialog>
</div>
